import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  message,
  Upload,
  Space,
} from "antd";
import { UploadOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchActiveCompany,
  editCompany,
} from "../../../core/redux/company.slice";
import { fetchCompanyTypes } from "../../../core/redux/companyType.slice";
import { photoService } from "../../../core/services/photo.service";
import SettingsSideBar from "../settingssidebar";
import CommonFooter from "../../../core/common/footer/commonFooter";
import StickyBar from "../../../core/common/StickyBar";
const { Option } = Select;
const fallbackImage = "/assets/img/users/user-00.png";
import { useTranslation } from "react-i18next";
const GeneralSettings = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { company, loading } = useSelector((state) => state.company);
  const { companyTypes } = useSelector((state) => state.companyType);

  const companyPhotos = (company?.photos || []).filter(
    (p) =>
      !p.productId &&
      !p.userId &&
      !p.customerId &&
      !p.supplierId &&
      !p.shopId &&
      !p.groupId &&
      !p.makerId &&
      !p.modelId,
  );

  const logoUrl =
    companyPhotos.find((p) => p.isPrincipal)?.presignedUrl ||
    companyPhotos[companyPhotos.length - 1]?.presignedUrl ||
    fallbackImage;

  useEffect(() => {
    dispatch(fetchActiveCompany());
    dispatch(fetchCompanyTypes());
  }, [dispatch]);

  useEffect(() => {
    if (company) {
      form.setFieldsValue({
        ...company,
        companyBirthdate: company.companyBirthdate
          ? dayjs(company.companyBirthdate)
          : null,
      });
    }else {
      form.resetFields(); 
    }
  }, [company, form]);

  const onFinish = (values) => {
    const companyData = {
      ...values,
      companyBirthdate: values.companyBirthdate?.toISOString(),
    };
    dispatch(editCompany({ id: company.companyId, company: companyData }))
      .unwrap()
      .then(() => {
        message.success(t("generalSettings.messages.updateSuccess"));
      })
      .catch((err) => {
        message.error(`${t("generalSettings.messages.updateError")}: ${err}`);
      });
  };

  const handleUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("companyId", company.companyId);
    formData.append("isPrincipal", "true");

    try {
      await photoService.uploadPhoto(formData);

      // Reload the company from the server → correct principal flags + fresh presigned URLs
      await dispatch(fetchActiveCompany()).unwrap();

      message.success(t("generalSettings.messages.uploadSuccess"));
    } catch (err) {
      console.error(t("generalSettings.messages.uploadfailed"), err);
      message.error(t("generalSettings.messages.uploadError"));
    }
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content settings-content">
          <StickyBar
            title={t("generalSettings.title")}
            actions={
              <Space size="middle">
                <Button className="btn-customized-primary" htmlType="submit" form="generalSettingsForm" loading={loading} icon={<SaveOutlined />}>
                  {t("common.save")}
                </Button>
              </Space>
            }
          />

          <div className="row">
            <div className="col-xl-12">
              <div className="settings-wrapper d-flex">
                <SettingsSideBar />
                <div className="card flex-fill mb-0">
                  <div className="card-header">
                    <h4 className="fs-18 fw-bold">
                      {t("generalSettings.title")}
                    </h4>
                  </div>
                  <div className="card-body">
                    <Form
                      form={form}
                      layout="vertical"
                      onFinish={onFinish}
                      id="generalSettingsForm"
                    >
                      <div className="card-title-head">
                        <h6 className="fs-16 fw-bold mb-3">
                          <span className="fs-16 me-2">
                            <i className="ti ti-user" />
                          </span>
                          {t("generalSettings.basicInfo")}
                        </h6>
                      </div>

                      <Form.Item label="">
                        <div className="d-flex flex-column align-items-center">
                          <img
                            src={logoUrl}
                            alt="logo"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = fallbackImage;
                            }}
                            style={{
                              height: 100,
                              width: 200,
                              marginBottom: 10,
                              border: "1px solid $primary",
                              borderRadius: "4px",
                              padding: "10px",
                              objectFit: "contain",
                            }}
                          />
                          <Upload
                            name="logo"
                            listType="picture"
                            showUploadList={false}
                            customRequest={({ file }) => handleUpload(file)}
                          >
                            <Button icon={<UploadOutlined />}>
                              {" "}
                              {t("generalSettings.changeLogo")}
                            </Button>
                          </Upload>
                        </div>
                      </Form.Item>

                      <div className="row mb-3">
                        <div className="col-md-4">
                          <Form.Item
                            name="companyName"
                            label={t("generalSettings.companyName")}
                            rules={[{ required: true }]}
                          >
                            <Input disabled="true" />
                          </Form.Item>
                        </div>
                        <div className="col-md-4">
                          <Form.Item
                            name="companyTaxNumber"
                            label={t("generalSettings.taxNumber")}
                            rules={[{ required: true }]}
                          >
                            <Input />
                          </Form.Item>
                        </div>
                        <div className="col-md-4">
                          <Form.Item
                            name="companyTypeId"
                            label={t("generalSettings.companyType")}
                            rules={[{ required: true }]}
                          >
                            <Select placeholder={t("generalSettings.select")}>
                              {companyTypes
                                .filter((type) => type.companyTypeIsActive)
                                .map((type) => (
                                  <Option
                                    key={type.companyTypeId}
                                    value={type.companyTypeId}
                                  >
                                    {type.companyTypeName}
                                  </Option>
                                ))}
                            </Select>
                          </Form.Item>
                        </div>
                        <div className="col-md-4">
                          <Form.Item
                            name="companyEmail"
                            label={t("generalSettings.email")}
                            rules={[{ required: true, type: "email" }]}
                          >
                            <Input />
                          </Form.Item>
                        </div>
                        <div className="col-md-4">
                          <Form.Item
                            name="companyPhone"
                            label={t("generalSettings.phone")}
                            rules={[
                              {
                                required: true,
                                message: t("generalSettings.phoneRequired"),
                              },
                              {
                                pattern: /^[+]?[0-9\s\-()]{8,15}$/,
                                message: t("generalSettings.phoneInvalid"),
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </div>
                        <div className="col-md-4">
                          <Form.Item
                            name="companyFax"
                            label={t("generalSettings.fax")}
                          >
                            <Input />
                          </Form.Item>
                        </div>
                      </div>
                      <div className="card-title-head">
                        <h6 className="fs-16 fw-bold mb-3">
                          <span className="fs-16 me-2">
                            <i className="ti ti-map-pin" />
                          </span>
                          {t("generalSettings.addressInfo")}
                        </h6>
                      </div>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <Form.Item
                            name="companyAddress"
                            label={t("generalSettings.address")}
                            rules={[{ required: true }]}
                          >
                            <Input />
                          </Form.Item>
                        </div>
                        <div className="col-md-3 mb-3">
                          <Form.Item
                            name="companyTown"
                            label={t("generalSettings.city")}
                            rules={[{ required: true }]}
                          >
                            <Input />
                          </Form.Item>
                        </div>
                        <div className="col-md-3 mb-3">
                          <Form.Item
                            name="companyPostalCode"
                            label={t("generalSettings.postalCode")}
                            rules={[{ required: true }]}
                          >
                            <Input />
                          </Form.Item>
                        </div>
                        <div className="col-md-4 mb-3">
                          <Form.Item
                            name="companyCountry"
                            label={t("generalSettings.country")}
                            rules={[{ required: true }]}
                          >
                            <Input />
                          </Form.Item>
                        </div>
                        <div className="col-md-4 mb-3">
                          <Form.Item
                            name="companyCurrency"
                            label={t("generalSettings.currency")}
                            rules={[{ required: true }]}
                          >
                            <Select placeholder={t("generalSettings.select")}>
                              <Option value="TND">Tunisian Dinar (TND)</Option>
                              <Option value="EUR">Euro (€)</Option>
                              <Option value="USD">Dollar ($)</Option>
                            </Select>
                          </Form.Item>
                        </div>
                        <div className="col-md-4 mb-3">
                          <Form.Item
                            name="companyBirthdate"
                            label={t("generalSettings.activityDate")}
                            rules={[{ required: true }]}
                          >
                            <DatePicker
                              format="YYYY-MM-DD"
                              style={{ width: "100%" }}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>
    </div>
  );
};

export default GeneralSettings;
